import { useState, useEffect } from "react";
import { homePath } from "../lib/ambito";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import GoogleButton from "../components/GoogleButton";
import {
  Compass,
  Ruler,
  Buildings,
  PaintBrush,
  ArrowRight,
  Envelope,
} from "@phosphor-icons/react";

const PROFESSIONI = [
  { key: "geometra", label: "Geometra", icon: Ruler },
  { key: "ingegnere", label: "Ingegnere", icon: Compass },
  { key: "architetto", label: "Architetto", icon: Buildings },
  { key: "designer", label: "Designer", icon: PaintBrush },
];

const OppureGoogle = () => (
  <div className="space-y-4 pt-2">
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-slate-200" />
      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">oppure</span>
      <div className="h-px flex-1 bg-slate-200" />
    </div>
    <GoogleButton />
  </div>
);

export default function Login() {
  const { login, register, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  const [tab, setTab] = useState(token ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [qualifica, setQualifica] = useState("geometra");
  const [loading, setLoading] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get(`/studio/invite/${token}`)
      .then((r) => {
        setInvitePreview(r.data);
        setEmail(r.data.email || "");
        setQualifica(r.data.qualifica_suggerita || "geometra");
      })
      .catch(() => setInvitePreview({ error: true }));
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate(homePath());
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await register(name, email, password, qualifica, token);
    setLoading(false);
    if (ok) navigate(homePath());
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#0B1F3A]" data-testid="login-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50cyUyMG1pbmltYWx8ZW58MHx8fHwxNzc2NzE2NzE4fDA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/90 via-[#12294A]/75 to-[#0B1F3A]/95" />

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-4" data-testid="login-brand">
            <img src="/icon-192.png" alt="GromApp" className="h-14 w-14 rounded-xl shadow-lg ring-1 ring-white/20" data-testid="login-brand-icon" />
            <div>
              <div className="font-heading font-extrabold tracking-tight text-3xl leading-none">
                Grom<span className="text-sky-200">App</span> <span className="font-normal text-slate-300">Studio</span>
              </div>
              <div className="text-[10px] text-slate-300 font-mono tracking-[0.25em] uppercase mt-1.5">Gestionale per studi tecnici</div>
            </div>
          </div>

          <div className="space-y-7 max-w-lg">
            <div className="h-px w-16 bg-sky-200/60" />
            <h1 className="font-heading text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.02]" data-testid="login-tagline">
              Lo strumento<br />dei geometri,<br />
              <span className="text-sky-200">da 2000 anni.</span>
            </h1>
            <p className="text-sm text-slate-300 leading-relaxed max-w-md">
              La <span className="text-white font-medium">groma</span> era la croce con cui gli agrimensori romani tracciavano città, strade e confini.
              Oggi diventa <span className="text-white font-medium">GromApp</span>: lo stesso rigore, in un&apos;app che gestisce incarichi, cantieri, computi, preventivi e scadenze in un unico posto.
            </p>

            <div className="grid grid-cols-4 gap-2 pt-1">
              {PROFESSIONI.map(({ key, label, icon: Icon }) => (
                <div key={key} className="border border-white/10 bg-white/5 backdrop-blur-sm rounded-md px-2.5 py-3 flex flex-col items-center text-center gap-1.5 hover:bg-white/10 transition-colors" data-testid={`login-prof-${key}`}>
                  <Icon size={18} weight="regular" className="text-sky-200" />
                  <div className="text-[11px] font-heading font-semibold text-white leading-tight">{label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 uppercase tracking-[0.2em]">
            <div>Groma <span className="text-slate-600">+</span> App <span className="text-slate-600">=</span> GromApp</div>
            <div>Incarichi · Cantieri · Computi · IA</div>
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md space-y-6">
          <div className="lg:hidden mb-6" data-testid="login-brand-mobile">
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="GromApp" className="h-11 w-11 rounded-lg shadow" />
              <div>
                <div className="font-heading font-extrabold text-2xl leading-none tracking-tight">
                  Grom<span className="text-sky-600">App</span> <span className="font-normal text-slate-500">Studio</span>
                </div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mt-1">Gestionale per studi tecnici</div>
              </div>
            </div>
            <p className="mt-4 font-heading text-lg font-bold tracking-tight text-slate-900 leading-snug">
              Lo strumento dei geometri, <span className="text-sky-700">da 2000 anni.</span>
            </p>
          </div>

          {invitePreview && !invitePreview.error && (
            <div className="border border-slate-200 bg-slate-50 rounded-md p-4" data-testid="invite-banner">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                  <Envelope size={16} weight="regular" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Invito ricevuto</div>
                  <div className="text-sm font-medium text-slate-900">
                    {invitePreview.inviter_name || "Un collega"} ti invita a <span className="font-heading">{invitePreview.studio_nome || "unirsi al suo studio"}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Completa la registrazione per accedere alle pratiche condivise.</div>
                </div>
              </div>
            </div>
          )}
          {invitePreview?.error && (
            <div className="border border-red-200 bg-red-50 text-red-700 rounded-md p-3 text-sm">
              Invito non valido o scaduto.
            </div>
          )}

          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">
              {tab === "login" ? "Accesso" : "Registrazione"}
            </div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
              {tab === "login" ? "Bentornato." : (token ? "Unisciti allo studio." : "Benvenuto in studio.")}
            </h2>
            <p className="text-sm text-slate-500 mt-2">
              {tab === "login"
                ? "Accedi per gestire lo studio."
                : "Crea il tuo profilo tecnico. Bastano pochi secondi."}
            </p>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
              <TabsTrigger value="login" data-testid="login-tab">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Registrati</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
                </div>
                {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                  {loading ? "Accesso in corso..." : (
                    <span className="inline-flex items-center gap-2">Accedi <ArrowRight size={14} weight="bold" /></span>
                  )}
                </Button>
              </form>
              <OppureGoogle />
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rname">Nome e cognome</Label>
                  <Input id="rname" placeholder="es. Alesjo Sinani" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rqual">Qualifica professionale</Label>
                  <Select value={qualifica} onValueChange={setQualifica}>
                    <SelectTrigger id="rqual" data-testid="register-qualifica-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="geometra">Geometra</SelectItem>
                      <SelectItem value="ingegnere">Ingegnere</SelectItem>
                      <SelectItem value="architetto">Architetto</SelectItem>
                      <SelectItem value="designer">Interior Designer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="remail">Email</Label>
                  <Input id="remail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={!!token} data-testid="register-email-input" />
                  {token && <div className="text-[11px] text-slate-500">Email bloccata dall&apos;invito.</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rpassword">Password (min 8 caratteri)</Label>
                  <Input id="rpassword" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="register-password-input" />
                </div>
                {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                  {loading ? "Creazione..." : (
                    <span className="inline-flex items-center gap-2">{token ? "Unisciti allo studio" : "Crea account"} <ArrowRight size={14} weight="bold" /></span>
                  )}
                </Button>
              </form>
              {!token && <OppureGoogle />}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
